/**
 * client/src/cma/usePasteFiles.ts — ⌘V as a third way into the same funnel.
 *
 * WHY. The intake screen has two ways to hand us a file: drag a file in, or press the button and
 * pick one. Both end at `addFiles(File[])`. Neither covers the thing an agent actually does most
 * often at a desk — takes a screenshot of the MLS sheet or the county record on screen, and
 * expects to paste it. Today that produces nothing at all: no error, no hint, just a keypress that
 * does not work, which reads as the app being broken rather than as a missing feature.
 *
 * This is a third caller of `addFiles`. It adds no new path through the app: everything a pasted
 * file meets afterwards — `classifyUpload`, `prepareSubjectPhoto`, the HEIC decode, the downscale,
 * the dedupe, the problem messages — is the code drop already uses. That is the whole design. A
 * paste handler that grew its own validation would be a second, quietly different, way for a file
 * to become evidence.
 *
 * WHERE THE LISTENER LIVES, AND WHY IT IS THE WINDOW. A dropzone is a `<div>`; it cannot hold
 * focus, so an element-scoped `onPaste` never fires unless the agent has tabbed to something inside
 * it. Nobody does that. The listener is therefore on `window`, with one guard: if the paste is
 * landing in a text field and the clipboard carries text, we do not touch it — pasting an address
 * into the address box must keep working exactly as it does now.
 *
 * THE NAME PROBLEM, AND WHY IT IS A PROVENANCE PROBLEM. A pasted screenshot has no filename. Chrome
 * hands over `image.png`; Safari sometimes hands over an empty string. Two consequences, and the
 * second is the one that matters:
 *
 *   1. `dedupe` keys on `${name}:${size}`, so two different pasted screenshots that happen to be
 *      the same byte length would collide and the second would vanish silently. So each paste is
 *      renamed with the time it arrived.
 *   2. A file the agent chose from disk carries a name they recognise and can trace back. A pasted
 *      screenshot carries nothing — not where it came from, not what it is a picture of. Under the
 *      product's own rule that is an unattributed source, and an unattributed source may not
 *      quietly become evidence. So `onPasted` is called with a note, the screen says the file was
 *      renamed and that its source is not established, and naming it stays the agent's job.
 *
 * The rename is a transform of the agent's evidence, and this file follows `preparePhoto`'s rule
 * about those: it is disclosed, never silent.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It ignores `text/html` and `text/plain` payloads. Pasting a
 * block of cells out of Excel or an MLS grid arrives as HTML plus tab-separated text, and turning
 * that into comps is a real feature with real consequences — it would be a fourth ingestion path,
 * it bypasses the extraction validator's cross-checks, and every figure it produced would need a
 * source the clipboard cannot supply. It is worth building. It is not worth building as a side
 * effect of adding paste support for screenshots. See the note returned by `pastedTextLooksTabular`.
 */
import { useEffect } from "react";

/** Chrome's placeholder name for clipboard images. Treated as "no name", not as a name. */
const ANONYMOUS_NAMES = new Set(["image.png", "image.jpeg", "image.jpg", "image", ""]);

function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

function extensionFor(file: File): string {
  const fromName = /\.([a-z0-9]+)$/i.exec(file.name)?.[1];
  if (fromName) return fromName.toLowerCase();
  const sub = (file.type || "").split("/")[1]?.toLowerCase();
  if (!sub) return "png";
  // "image/jpeg" -> jpg reads better in a file list than jpeg; "image/svg+xml" -> svg.
  if (sub === "jpeg") return "jpg";
  return sub.split("+")[0];
}

/**
 * A pasted file, renamed so it can be told apart from the next one and so the dedupe key is unique.
 * A file that arrived with a real name of its own — some apps do supply one — keeps it.
 */
export function nameForPastedFile(file: File, now: Date = new Date()): string {
  if (!ANONYMOUS_NAMES.has(file.name.toLowerCase())) return file.name;
  return `pasted-${stamp(now)}.${extensionFor(file)}`;
}

/** True when the paste should be left entirely alone: the agent is typing into a field. */
export function pasteBelongsToAField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return !!el.closest("input, textarea, select, [contenteditable='true']");
}

/**
 * Recognises a clipboard payload that is a grid of cells rather than a file. We do not ingest it;
 * we say so, because silently ignoring a paste of twelve comps is the same failure as silently
 * ignoring a paste of a screenshot.
 */
export function pastedTextLooksTabular(text: string): boolean {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return false;
  const tabbed = lines.filter((l) => l.includes("\t")).length;
  return tabbed >= 2 && tabbed >= lines.length / 2;
}

export interface PasteHandlers {
  /** The files, already renamed. Hand them straight to `addFiles`. */
  onPastedFiles: (files: File[], notes: string[]) => void;
  /** Called when the clipboard held a grid of cells we are choosing not to parse. */
  onPastedTable?: (note: string) => void;
  /** Off while a build is running, so a stray ⌘V cannot change the inputs mid-flight. */
  enabled?: boolean;
}

const TABLE_NOTE =
  "That looks like a block of spreadsheet cells rather than a file. We have not read it — a pasted " +
  "grid carries no record ID or source, and every figure here has to name where it came from. " +
  "Export the rows to a CSV or Excel file and drop that in, or paste a screenshot and type the " +
  "figures you need.";

export function usePasteFiles({ onPastedFiles, onPastedTable, enabled = true }: PasteHandlers): void {
  useEffect(() => {
    if (!enabled) return;

    function handle(event: ClipboardEvent) {
      const data = event.clipboardData;
      if (!data) return;

      const files: File[] = [];
      // `items` is the reliable surface for clipboard images; `files` is empty in some browsers.
      // Only `kind === "file"` is taken — a text/html payload can carry remote image references and
      // is not something we fetch.
      const items = data.items ? Array.from(data.items) : [];
      for (const item of items) {
        if (item.kind !== "file") continue;
        const file = item.getAsFile();
        if (file) files.push(file);
      }
      if (!files.length && data.files?.length) files.push(...Array.from(data.files));

      if (!files.length) {
        const text = data.getData?.("text/plain") ?? "";
        if (text && pastedTextLooksTabular(text) && !pasteBelongsToAField(event.target)) {
          event.preventDefault();
          onPastedTable?.(TABLE_NOTE);
        }
        return; // a plain text paste is none of our business
      }

      // A file paste into a text field is still a file paste — a screenshot dropped on the address
      // box is not an address. But a text paste into a field is left alone entirely, above.
      event.preventDefault();

      const now = new Date();
      const notes: string[] = [];
      const renamed = files.map((file) => {
        const name = nameForPastedFile(file, now);
        if (name === file.name) return file;
        notes.push(
          `Pasted image saved as ${name}. A pasted file arrives without a name or a source, so ` +
          "this one is not attributed to anything yet — say where it came from before it is cited.",
        );
        return new File([file], name, { type: file.type, lastModified: file.lastModified });
      });

      onPastedFiles(renamed, notes);
    }

    window.addEventListener("paste", handle as EventListener);
    return () => window.removeEventListener("paste", handle as EventListener);
  }, [onPastedFiles, onPastedTable, enabled]);
}
